import apiComunicacao from '@modules/solicitacoes/api/apiComunicacao'
// mergeUpdatedConversas é helper PURO (sem store) — reusado por import direto.
import { mergeUpdatedConversas } from '@modules/atendimento/utils/conversa-helpers'

// Sequência monotônica local pra desambiguar ids otimistas criados no mesmo ms.
// Sem PRNG (Math.random) — é id temporário de UI, só precisa ser único na sessão.
let _optimisticSeq = 0
const buildOptimisticMessage = (id_conversa, params) => ({
  id: 'optimistic-' + Date.now() + '-' + ++_optimisticSeq,
  id_conversa,
  direcao: 'saida',
  tipo: params.tipo || 'texto',
  conteudo: params.conteudo || '',
  is_note: params.is_note || false,
  reply_to_message_id: params.reply_to_message_id || null,
  media_url: params.media_url || null,
  media_mime_type: params.media_mime_type || null,
  media_filename: params.media_filename || null,
  media_size_bytes: params.media_size_bytes || null,
  status: 'enviando',
  data_mensagem: new Date().toISOString(),
  _optimistic: true,
})

// `_silent: true` pula o commit de SET_LOADING_CONVERSAS — usado em
// refresh periódico em background (sync/polling) pra não piscar o skeleton
// quando o backend ainda está populando e retorna vazio. Primeira carga e
// ações de usuário (filtro, busca) chamam SEM _silent pra mostrar feedback.
// Define o modo da view adaptativa. Comita no store (fonte de verdade que as
// actions leem em cada chamada) e propaga para a api — os disparos diretos do
// poller/serviço de chat compartilhado não passam por estas actions, então a
// api precisa conhecer o modo ativo para devolver o fixture set correto.
export const setMode = ({ commit }, mode) => {
  commit('SET_MODE', mode)
  apiComunicacao.setActiveMode(mode)
}

export const listarConversas = async ({ commit, state }, params = {}) => {
  const { _silent, ...apiParams } = params
  if (!_silent) commit('SET_LOADING_CONVERSAS', true)
  try {
    const r = await apiComunicacao.listarConversas({
      ...apiParams,
      mode: state.mode,
    })
    if (r && r.status === 200) {
      const conversas = (r.value && r.value.conversas) || r.value || []
      const list = Array.isArray(conversas) ? conversas : []
      commit('SET_CONVERSAS', list)
    }
    return r
  } finally {
    if (!_silent) commit('SET_LOADING_CONVERSAS', false)
  }
}

export const limparConversas = ({ commit }) => {
  commit('SET_CONVERSAS', [])
}

// Trigger de criação de conversa: garante (find-or-create) a conversa de cada
// solicitação ativa do escritório da sessão. Chamado ao abrir a Comunicação,
// ANTES de listar — assim solicitações abertas sem chat ainda já aparecem.
// Idempotente no backend (não duplica). Falha não bloqueia a listagem.
export const sincronizarConversas = async () => {
  try {
    return await apiComunicacao.sincronizarConversas()
  } catch {
    return { status: 500, message: 'Falha ao sincronizar conversas.', value: null }
  }
}

export const listarMensagens = async ({ commit, state }, params = {}) => {
  const apiParams = { ...params, mode: state.mode }
  // When loading older messages (before_id), skip SET_MENSAGENS to avoid
  // replacing current messages. The caller (chat-window) will prepend.
  if (params.before_id) {
    const r = await apiComunicacao.listarMensagens(apiParams)
    return r
  }
  commit('SET_LOADING_MENSAGENS', true)
  try {
    const r2 = await apiComunicacao.listarMensagens(apiParams)
    if (r2 && r2.status === 200) {
      const mensagens = (r2.value && r2.value.mensagens) || r2.value || []
      commit('SET_MENSAGENS', Array.isArray(mensagens) ? mensagens : [])
    }
    return r2
  } finally {
    commit('SET_LOADING_MENSAGENS', false)
  }
}

export const pollConversas = async ({ commit, state }, params = {}) => {
  const r = await apiComunicacao.pollConversas({ ...params, mode: state.mode })
  if (r && r.status === 200 && r.value) {
    const updated = r.value.conversas_atualizadas || []
    if (updated.length > 0) {
      const current =
        (state.conversas && state.conversas.data) || state.conversas || []
      commit('SET_CONVERSAS', mergeUpdatedConversas(current, updated))
    }
  }
  return r
}

export const selectConversa = ({ commit }, conversa) => {
  commit('SET_MENSAGENS', [])
  commit('SET_SELECTED_CONVERSA', conversa)
  commit('SET_REPLY_TO', null)
  commit('SET_NOTE_MODE', false)
  commit('SET_EDITING_MESSAGE', null)
  commit('SET_UPLOAD_PROGRESS', null)
}

export const enviarMensagem = async ({ commit, state }, params = {}) => {
  const id_conversa = state.selectedConversa
    ? state.selectedConversa.id
    : params.id_conversa

  const optimistic = buildOptimisticMessage(id_conversa, params)
  const tempId = optimistic.id
  commit('ADD_MENSAGEM', optimistic)

  try {
    const r = await apiComunicacao.enviarMensagem({
      id_conversa,
      tipo: params.tipo,
      conteudo: params.conteudo,
      is_note: params.is_note || false,
      reply_to_message_id: params.reply_to_message_id || null,
      media_url: params.media_url || null,
      media_mime_type: params.media_mime_type || null,
      media_filename: params.media_filename || null,
      media_size_bytes: params.media_size_bytes || null,
      mode: state.mode,
    })
    if (r && r.status === 200 && r.value) {
      commit('REPLACE_OPTIMISTIC_MENSAGEM', { temp_id: tempId, real: r.value })
      commit('UPDATE_CONVERSA', {
        id: id_conversa,
        ultima_mensagem_em: r.value.data_mensagem,
        ultima_mensagem_preview: r.value.conteudo || '',
      })
    } else {
      // Keep the bubble but mark as error so the user can retry
      commit('UPDATE_MENSAGEM', { id: tempId, status: 'erro' })
    }
    return r
  } catch (e) {
    commit('UPDATE_MENSAGEM', { id: tempId, status: 'erro' })
    throw e
  }
}

export const checkDeliveryStatuses = async ({ commit, state }) => {
  const selected = state.selectedConversa
  if (!selected || !selected.id) return
  try {
    const r = await apiComunicacao.getDeliveryStatus({
      id_conversa: selected.id,
    })
    if (r && r.status === 200 && Array.isArray(r.value)) {
      commit('APPLY_DELIVERY_STATUSES', r.value)
    }
  } catch {
    // Polling de delivery status é best-effort; se falhar, os checks
    // continuam com o último valor conhecido até o próximo ciclo.
  }
}

export const marcarComoLida = async ({ commit }, params) => {
  if (!params) params = {}
  // Optimistic update: clear badge immediately (don't wait for API)
  commit('UPDATE_CONVERSA', {
    id: params.id_conversa,
    mensagens_nao_lidas: 0,
  })
  // Fire-and-forget API call
  try {
    await apiComunicacao.marcarComoLida(params)
  } catch {
    // Já aplicamos o update otimista no commit acima — uma falha aqui
    // será reconciliada no próximo poll (que reflete o estado real).
  }
}

export const uploadMedia = async (
  { commit, state },
  { id_conversa, tipo, arquivo, mode } = {}
) => {
  commit('SET_UPLOAD_PROGRESS', 0)
  const r = await apiComunicacao.uploadMedia({
    id_conversa:
      id_conversa ||
      (state.selectedConversa ? state.selectedConversa.id : null),
    tipo,
    arquivo,
    mode,
    onProgress: function (percent) {
      commit('SET_UPLOAD_PROGRESS', percent)
    },
  })
  commit('SET_UPLOAD_PROGRESS', null)
  return r
}

export const editarMensagem = async (
  { commit },
  { id_mensagem, conteudo } = {}
) => {
  const r = await apiComunicacao.editarMensagem({ id_mensagem, conteudo })
  if (r && r.status === 200 && r.value) {
    commit('UPDATE_MENSAGEM', r.value)
  }
  commit('SET_EDITING_MESSAGE', null)
  return r
}

export const removerMensagem = async ({ commit }, { id_mensagem } = {}) => {
  const r = await apiComunicacao.removerMensagem({ id_mensagem })
  if (r && r.status === 200) {
    commit('REMOVE_MENSAGEM', id_mensagem)
  }
  return r
}

export const importarHistorico = async (
  { commit, state },
  { id_conversa, quantidade } = {}
) => {
  const conversaId =
    id_conversa || (state.selectedConversa ? state.selectedConversa.id : null)
  const r = await apiComunicacao.importarHistorico({
    id_conversa: conversaId,
    quantidade,
  })
  return r
}

export const reagirMensagem = async (
  { commit, state },
  { id_mensagem, emoji } = {}
) => {
  // Save current reactions for rollback
  const msg = state.mensagens.find((m) => m.id === id_mensagem)
  const previousReacoes = msg ? (msg.reacoes || []).slice() : []

  // Optimistic: show reaction immediately
  const optimisticReacoes = previousReacoes.concat([{ emoji, by: 'Você' }])
  commit('UPDATE_MENSAGEM', { id: id_mensagem, reacoes: optimisticReacoes })

  const r = await apiComunicacao.reagirMensagem({ id_mensagem, emoji })
  if (r && r.status === 200 && r.value) {
    // Replace with server-confirmed reactions
    commit('UPDATE_MENSAGEM', {
      id: id_mensagem,
      reacoes: r.value.reacoes || optimisticReacoes,
    })
  } else {
    // Rollback on error
    commit('UPDATE_MENSAGEM', { id: id_mensagem, reacoes: previousReacoes })
  }
  return r
}

export const criarConversa = (context, { id_conexao, telefone, nome } = {}) => {
  return apiComunicacao.novaConversa({ id_conexao, telefone, nome })
}

export const buscarContatos = (context, { q } = {}) => {
  return apiComunicacao.buscarContatos({ q })
}

export const setReplyTo = ({ commit }, message) => {
  commit('SET_EDITING_MESSAGE', null)
  commit('SET_REPLY_TO', message)
}

export const setNoteMode = ({ commit }, active) => {
  commit('SET_NOTE_MODE', active)
}

export const setEditingMessage = ({ commit }, message) => {
  commit('SET_REPLY_TO', null)
  commit('SET_EDITING_MESSAGE', message)
}

export const cancelReply = ({ commit }) => {
  commit('SET_REPLY_TO', null)
}

export const cancelEdit = ({ commit }) => {
  commit('SET_EDITING_MESSAGE', null)
}

export const prependMensagens = ({ commit }, mensagens) => {
  commit('PREPEND_MENSAGENS', mensagens)
}

// Shared helper for conversa status mutations (atribuir/fechar/escalar).
// Commits UPDATE_CONVERSA with the given patch and, when the affected
// conversation is the currently selected one, mirrors the patch into
// SET_SELECTED_CONVERSA so the open chat reflects the new status.
const updateConversaStatus = ({ commit, state }, { id_conversa, patch }) => {
  commit('UPDATE_CONVERSA', { id: id_conversa, ...patch })
  if (state.selectedConversa && state.selectedConversa.id === id_conversa) {
    commit('SET_SELECTED_CONVERSA', { ...state.selectedConversa, ...patch })
  }
}

export const atribuirConversa = async ({ commit, state }, params) => {
  const id_conversa = params.id_conversa
  const r = await apiComunicacao.atribuirConversa({ id_conversa: id_conversa })
  if (r && r.status === 200 && r.value) {
    updateConversaStatus({ commit, state }, { id_conversa, patch: r.value })
  }
  return r
}

export const fecharConversa = async ({ commit, state }, params) => {
  const id_conversa = params.id_conversa
  const r = await apiComunicacao.fecharConversa({ id_conversa: id_conversa })
  if (r && r.status === 200) {
    updateConversaStatus(
      { commit, state },
      { id_conversa, patch: { status: 'fechada' } }
    )
  }
  return r
}

export const escalarConversa = async ({ commit, state }, params) => {
  const id_conversa = params.id_conversa
  const nota = params.nota || ''
  const r = await apiComunicacao.escalarConversa({
    id_conversa: id_conversa,
    nota: nota,
  })
  if (r && r.status === 200) {
    updateConversaStatus(
      { commit, state },
      { id_conversa, patch: { status: 'escalada' } }
    )
  }
  return r
}

// Handler compartilhado pelos listeners de SOLICITACOES_COMUNICACAO_CONVERSA_CRIADA.
// NÃO chamar listarConversas aqui: a nova conversa pode estar fora da página 1
// (sem mensagem, ordenação por ultima_mensagem_em DESC com null no fim) e
// listarConversas poderia fechar o chat recém-aberto. pollConversas faz merge
// (preserva a seleção).
export const handleConversaCriada = async ({ dispatch }, data) => {
  if (!data || !data.id) return
  await dispatch('selectConversa', data)
  await dispatch('listarMensagens', { id_conversa: data.id })
  if (data.mensagens_nao_lidas && data.mensagens_nao_lidas > 0) {
    dispatch('marcarComoLida', { id_conversa: data.id })
  }
  dispatch('pollConversas')
}

export const transferirConversa = async ({ dispatch }, params) => {
  const id_conversa = params.id_conversa
  const para_profissional_id = params.para_profissional_id
  const nota = params.nota || ''
  const r = await apiComunicacao.transferirConversa({
    id_conversa: id_conversa,
    para_profissional_id: para_profissional_id,
    nota: nota,
  })
  if (r && r.status === 200) {
    dispatch('listarConversas')
  }
  return r
}

export const listarRespostasRapidas = async ({ commit }) => {
  const r = await apiComunicacao.listarRespostasRapidas()
  if (r && r.status === 200) {
    commit('SET_RESPOSTAS_RAPIDAS', r.value || [])
  }
  return r
}

export const setFiltroStatus = ({ commit }, status) => {
  commit('SET_FILTRO_STATUS', status)
}

export const setFiltroProfissional = ({ commit }, profissional) => {
  commit('SET_FILTRO_PROFISSIONAL', profissional)
}

// ====================================================================
// Solicitações REAIS (backend aawz_solicitacoes)
// ====================================================================
// A orquestração das cargas reais (catálogo/solicitações/detalhe) vive na camada de API
// (apiSolicitacoes.carregar*Real), chamada direto pelos componentes — fluxo componente→API→
// store→getter. Aqui ficam só os setters (mirror de UI-state, consumidos pela própria API via
// store.dispatch) e a definição de sessão/contexto.
export const setSolicitacoes = ({ commit }, payload) => {
  commit('SET_SOLICITACOES', payload)
}
export const setSolicitacoesLoading = ({ commit }, payload) => {
  commit('SET_SOLICITACOES_LOADING', payload)
}

export const setAreas = ({ commit }, payload) => {
  commit('SET_AREAS', payload)
}
export const setAreasLoading = ({ commit }, payload) => {
  commit('SET_AREAS_LOADING', payload)
}

export const setTipos = ({ commit }, payload) => {
  commit('SET_TIPOS', payload)
}
export const setTiposLoading = ({ commit }, payload) => {
  commit('SET_TIPOS_LOADING', payload)
}

// Catálogo BACKOFFICE (esteira interna) — slot ADITIVO (US-5); nunca comita
// SET_TIPOS/SET_AREAS (catálogo estratégico usado por outros módulos).
export const setCatalogoBackofficeTipos = ({ commit }, payload) => {
  commit('SET_CATALOGO_BACKOFFICE_TIPOS', payload)
}
export const setCatalogoBackofficeAreas = ({ commit }, payload) => {
  commit('SET_CATALOGO_BACKOFFICE_AREAS', payload)
}
export const setCatalogoBackofficeLoading = ({ commit }, payload) => {
  commit('SET_CATALOGO_BACKOFFICE_LOADING', payload)
}

export const setTarefas = ({ commit }, payload) => {
  commit('SET_TAREFAS', payload)
}
export const setTarefasLoading = ({ commit }, payload) => {
  commit('SET_TAREFAS_LOADING', payload)
}

export const setArquivos = ({ commit }, payload) => {
  commit('SET_ARQUIVOS', payload)
}
export const setArquivosLoading = ({ commit }, payload) => {
  commit('SET_ARQUIVOS_LOADING', payload)
}

// --- OPERACAO_AAWZ (AAWZ profissional, cross-office). Setters (mirror de UI-state) + patches otimistas,
// consumidos pela apiSolicitacoesOperacaoAawz/apiSolicitacoesOperacaoAawzArquivos via store.dispatch
// (safeDispatch). Cada action só comita a mutation correspondente (padrão do módulo).
export const setOperacaoAawzSolicitacoes = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_SOLICITACOES', payload)
}
export const setOperacaoAawzSolicitacoesLoading = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_SOLICITACOES_LOADING', payload)
}
export const setOperacaoAawzArvores = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_ARVORES', payload)
}

export const setOperacaoAawzBoardTasks = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_BOARD_TASKS', payload)
}

export const setOperacaoAawzBoardTasksLoading = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_BOARD_TASKS_LOADING', payload)
}
export const setOperacaoAawzDetalheSolicitacao = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_DETALHE_SOLICITACAO', payload)
}
export const setOperacaoAawzDetalheArvore = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_DETALHE_ARVORE', payload)
}
export const setOperacaoAawzDetalheArquivos = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_DETALHE_ARQUIVOS', payload)
}
export const setOperacaoAawzDetalheLoading = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_DETALHE_LOADING', payload)
}
export const setOperacaoAawzDetalheErro = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_DETALHE_ERRO', payload)
}
export const setOperacaoAawzDetalheArvoreCarregando = ({ commit }, payload) => {
  commit('SET_OPERACAO_AAWZ_DETALHE_ARVORE_CARREGANDO', payload)
}
export const patchOperacaoAawzDetalheStatus = ({ commit }, payload) => {
  commit('PATCH_OPERACAO_AAWZ_DETALHE_STATUS', payload)
}
export const patchOperacaoAawzDetalheEtapa = ({ commit }, payload) => {
  commit('PATCH_OPERACAO_AAWZ_DETALHE_ETAPA', payload)
}
// ====================================================================
// Configurações (Settings > Solicitações — visão INTERNA AAWZ)
// ====================================================================
// Setters (mirror de UI-state, consumidos pela própria API via store.dispatch).
// A orquestração das cargas vive em apiConfiguracoes (fluxo componente→API→store→getter);
// estas actions NUNCA fazem fetch — só comitam.
export const setConfigTipos = ({ commit }, payload) => {
  commit('SET_CONFIG_TIPOS', payload)
}
export const setConfigTiposLoading = ({ commit }, payload) => {
  commit('SET_CONFIG_TIPOS_LOADING', payload)
}

export const setConfigVinculos = ({ commit }, payload) => {
  commit('SET_CONFIG_VINCULOS', payload)
}
export const setConfigVinculosLoading = ({ commit }, payload) => {
  commit('SET_CONFIG_VINCULOS_LOADING', payload)
}

export const setConfigConexoes = ({ commit }, payload) => {
  commit('SET_CONFIG_CONEXOES', payload)
}
export const setConfigConexoesLoading = ({ commit }, payload) => {
  commit('SET_CONFIG_CONEXOES_LOADING', payload)
}

// Aplicação otimista de um vínculo tipo×conexão (a API chama antes do POST e
// reverte com setConfigVinculos no erro).
export const upsertConfigVinculo = ({ commit }, payload) => {
  commit('UPSERT_CONFIG_VINCULO', payload)
}

// --- Matriz serviço×etapa (obrigatoriedade por estágio). Setters (mirror de
// UI-state, consumidos pela apiConfiguracoes via store.dispatch); NUNCA fazem
// fetch — só comitam.
export const setConfigVinculosEstagio = ({ commit }, payload) => {
  commit('SET_CONFIG_VINCULOS_ESTAGIO', payload)
}
export const setConfigVinculosEstagioLoading = ({ commit }, payload) => {
  commit('SET_CONFIG_VINCULOS_ESTAGIO_LOADING', payload)
}
// Aplicação otimista de um vínculo serviço×etapa (a API chama antes do PUT e
// reverte com setConfigVinculosEstagio no erro).
export const upsertConfigVinculoEstagio = ({ commit }, payload) => {
  commit('UPSERT_CONFIG_VINCULO_ESTAGIO', payload)
}

// Define a sessão/contexto ativo (escritório + profissional logado), fixado no shell do módulo.
export const definirSessao = ({ commit }, payload) => {
  commit('SET_SESSAO', payload || null)
}

// --- Pergunta pendente do Consultor Estratégico (spec-008). Setters finos: quem grava é a
// faixa da Jornada, antes de navegar; quem consome é a casca do Consultor, no created —
// leitura pelo getter (síncrona) seguida do consumo, para a pergunta não reaparecer num F5.
export const setPerguntaPendenteConsultor = ({ commit }, payload) => {
  commit('SET_PERGUNTA_PENDENTE_CONSULTOR', payload || '')
}

export const consumirPerguntaPendenteConsultor = ({ commit }) => {
  commit('SET_PERGUNTA_PENDENTE_CONSULTOR', '')
}

// --- Painel lateral do Consultor no Hub. Abrir com pergunta é o caminho dos chips
// da faixa da Jornada: a pergunta viaja pelo MESMO canal do `perguntaPendente`, e
// o painel a envia sozinho (o clique no chip já é a intenção de perguntar).
export const abrirPainelConsultor = ({ commit }, pergunta) => {
  commit('SET_PERGUNTA_PENDENTE_CONSULTOR', pergunta || '')
  commit('SET_CONSULTOR_PAINEL_ABERTO', true)
}

export const fecharPainelConsultor = ({ commit }) => {
  commit('SET_CONSULTOR_PAINEL_ABERTO', false)
}
